import React, { FC } from "react"
import { Alert, Table } from "react-bootstrap"
import { useSelector } from "react-redux"

import {
  getFeatures,
  getPerformanceSummary,
  getProductNetCurrencyCode,
  getProductRackCurrencyCode,
} from "@/onboarding/features/product/selectors"
import { formatPercentage } from "@/helpers/number"
import Hidden from "@/components/Hidden"

const SummaryDataRow = ({
  label,
  summary,
  showFinancials,
}: {
  label: string
  summary: any
  showFinancials: boolean
}): JSX.Element => {
  const targetMet = summary.currentMargin.fractional >= summary.marginTarget.fractional

  return (
    <tr>
      <td>{label}</td>
      <td className="text-right">{summary.expectedAdults}</td>
      <td className="text-right">{summary.expectedChildren}</td>
      <td className="text-right">{summary.expectedGuests}</td>
      <Hidden unless={showFinancials}>
        <td className="text-right">{summary.salesTarget.formatted}</td>
        <td className="text-right">
          {`${summary.marginTarget.formatted} (${formatPercentage(
            summary.marginTargetPercentage
          )})`}
        </td>
        <td className={targetMet ? "text-success text-right" : "text-danger text-right"}>
          {`${summary.currentMargin.formatted} (${formatPercentage(
            summary.currentMarginPercentage
          )})`}
        </td>
      </Hidden>
    </tr>
  )
}

const TargetSummary: FC = () => {
  const features = useSelector(getFeatures)
  const summary = useSelector(getPerformanceSummary)
  const netCurrencyCode = useSelector(getProductNetCurrencyCode)
  const rackCurrencyCode = useSelector(getProductRackCurrencyCode)

  // Financial forecasts require a single currency. When tour cost and sell
  // rate currencies differ, cross-currency figures would be misleading
  // without FX conversion, so hide them and explain why.
  const crossCurrency = netCurrencyCode !== rackCurrencyCode
  const showFinancials = features.general.product.forecasts.showFinancialTargets && !crossCurrency

  return (
    <>
      {features.general.product.forecasts.showFinancialTargets && crossCurrency && (
        <Alert variant="info" className="mb-2">
          Financial forecasts are unavailable because Tour Cost Currency ({netCurrencyCode}) and
          Sell Rate Currency ({rackCurrencyCode}) differ.
        </Alert>
      )}
      <Table>
        <thead>
          <tr>
            <th scope="col" aria-label="Timeframe" />
            <th scope="col" className="text-right">
              Expected Adults
            </th>
            <th scope="col" className="text-right">
              Expected Children
            </th>
            <th scope="col" className="text-right">
              Expected Total Guests
            </th>
            <Hidden unless={showFinancials}>
              <th scope="col" className="text-right">
                Sales Target
              </th>
              <th scope="col" className="text-right">
                Margin Target
              </th>
              <th scope="col" className="text-right">
                Current Margin
              </th>
            </Hidden>
          </tr>
        </thead>
        <tbody>
          <SummaryDataRow
            label="Per Call"
            summary={summary.daily}
            showFinancials={showFinancials}
          />
          <SummaryDataRow
            label="Per Season"
            summary={summary.season}
            showFinancials={showFinancials}
          />
        </tbody>
      </Table>
    </>
  )
}

export default TargetSummary
